/**
 * 직원 메일함 패널 (관리자) — "이 사람 편지함에 뭐가 있나" 에 답하는 화면.
 *
 * <h3>'전체 메일'(AdminMailPanel) 과 무엇이 다른가</h3>
 * 전체 메일은 mail_message 를 회사 전체 시간순으로 훑는다. 특정 인물을 조사할 때 검색으로
 * 좁혀도 <b>그 사람이 그 메일을 어디에 두었는지</b> — 스팸으로 넣었는지, 지웠는지, 아직 안 읽었는지
 * — 는 알 수 없다. 그건 mail_user_state 에만 있는 정보다.
 * 이 화면은 대상 직원의 user_state 를 기준으로, 그 사람이 보는 폴더 구성·정렬 그대로 재현한다.
 * (퇴사자 인수인계 / 수신 누락 조사 / 분쟁 확인 용도)
 *
 * <h3>흐름</h3>
 *   직원 선택(사내 주소록 자동완성) → 폴더 탭(카운트 포함) → 제목·보낸이 검색 → 행 클릭 = 관리자 열람
 *
 * <h3>감사</h3>
 * 목록 조회는 서버가 READ_OTHER 를 <b>페이지당 1건</b>(대상 직원 id 포함) 남기고,
 * 행 클릭 상세는 getMailAsAdmin 이 건별 READ_OTHER 를 남긴다. 카운트 조회는 숫자뿐이라 남기지 않는다.
 */
import React, { useEffect, useState } from "react";
import {
    Box, Chip, IconButton, Paper, Stack, Tab, TableBody, TableCell, TableHead,
    TableRow, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import SearchRoundedIcon      from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon     from "@mui/icons-material/RefreshRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import StarRoundedIcon        from "@mui/icons-material/StarRounded";

import { mailApi }        from "../../api/mailApi";
import { notify }         from "../../../../util/toast";
import { ds }             from "../../../../styles/designSystem";
import AppAutocomplete    from "../../../../component/common/AppAutocomplete";
import LoadingDots        from "../../../../component/common/LoadingDots";
import TablePaginator, { DEFAULT_PAGE_SIZE, TableSizeSelect } from "../../../../component/common/TablePaginator";
import ResizableTable     from "../../../../component/common/ResizableTable";
import DeliveryStatusBadge from "../DeliveryStatusBadge";
import { formatFileSize } from "../../../../util/numberFormat";
import dayjs from "dayjs";

/**
 * 폴더 탭 — 사이드바(FOLDERS)와 같은 이름을 쓰되 <b>실제 폴더만</b> 둔다.
 * 사용자 사이드바의 '내게 쓴 메일함'·'예약 발송 이력' 같은 가상 폴더는 여기 없다:
 * 그건 폴더가 아니라 별도 쿼리라, 서버 mailbox endpoint 의 folder 값으로 표현되지 않는다.
 *
 * countKeys — 카운트 응답(total/unread)에서 합산할 폴더 키. '전체'는 서버의 전체 보기와 같은
 * 구성(INBOX+CUSTOM+ARCHIVE)이어야 탭 숫자와 목록 건수가 어긋나지 않는다.
 */
const TABS = [
    { key: "ALL",       label: "전체",        countKeys: ["INBOX", "CUSTOM", "ARCHIVE"] },
    { key: "INBOX",     label: "받은 메일함", countKeys: ["INBOX"] },
    { key: "SENT",      label: "보낸 메일함", countKeys: ["SENT"] },
    { key: "DRAFT",     label: "임시 보관함", countKeys: ["DRAFT"] },
    { key: "SCHEDULED", label: "예약함",      countKeys: ["SCHEDULED"] },
    { key: "SPAM",      label: "스팸함",      countKeys: ["SPAM"] },
    { key: "TRASH",     label: "휴지통",      countKeys: ["TRASH"] },
    { key: "ARCHIVE",   label: "보관함",      countKeys: ["ARCHIVE"] },
    { key: "CUSTOM",    label: "내 폴더",     countKeys: ["CUSTOM"] },
];

/** 목록 '폴더' 열 표기 — 커스텀 폴더는 폴더명을 그대로 보여준다(어디에 정리해뒀는지가 정보다). */
const FOLDER_LABEL = {
    INBOX: "받은 메일함", SENT: "보낸 메일함", DRAFT: "임시 보관함",
    SCHEDULED: "예약함", SPAM: "스팸함", TRASH: "휴지통", ARCHIVE: "보관함", CUSTOM: "내 폴더",
};

const fmt = (v) => (v ? dayjs(v).format("YYYY.MM.DD HH:mm") : "-");

const sumCounts = (map, keys) => keys.reduce((acc, k) => acc + (map?.[k] || 0), 0);

export default function EmployeeMailboxPanel({ onOpenMail }) {
    const [employee, setEmployee] = useState(null);   // CompanyMemberDto | null
    const [counts, setCounts]     = useState(null);   // { total: {}, unread: {} , …인적사항 }

    const [tab, setTab]         = useState("ALL");
    const [q, setQ]             = useState("");
    const [rows, setRows]       = useState([]);
    const [total, setTotal]     = useState(0);
    const [page, setPage]       = useState(0);
    const [size, setSize]       = useState(DEFAULT_PAGE_SIZE);
    const [loading, setLoading] = useState(false);

    const employeeId = employee?.id ?? null;

    const loadCounts = async (empId) => {
        if (!empId) { setCounts(null); return; }
        try {
            const res = await mailApi.adminMailboxCounts(empId);
            setCounts(res?.data ?? res);
        } catch (e) {
            // 카운트는 보조 정보다 — 실패해도 목록은 계속 볼 수 있어야 하므로 화면을 막지 않는다.
            setCounts(null);
            notify(e?.response?.data?.message || "폴더 카운트를 불러오지 못했습니다", "error");
        }
    };

    const load = async (overridePage = null) => {
        if (!employeeId) { setRows([]); setTotal(0); return; }
        setLoading(true);
        try {
            const p = overridePage != null ? overridePage : page;
            const res = await mailApi.adminMailbox(employeeId, tab, q, p, size);
            const data = res?.data ?? res;
            setRows(data?.content || []);
            setTotal(data?.totalElements || 0);
        } catch (e) {
            notify(e?.response?.data?.message || "메일함을 불러오지 못했습니다", "error");
            setRows([]); setTotal(0);
        } finally { setLoading(false); }
    };

    /**
     * 직원 선택 — 폴더·검색어도 <b>같은 렌더에서 함께</b> 되돌린다.
     *
     * <p>앞 사람의 '휴지통 + 검색어' 가 남아 있으면 새로 고른 사람의 화면이 비어 보여
     * "이 사람은 메일이 없네" 로 잘못 읽힌다. 이 초기화를 effect 로 미루면 재조회 effect 가
     * <b>이전 폴더로 한 번 더</b> 나가는데, 그 요청은 관리자가 실제로 보지도 않은 폴더를
     * 감사 로그(READ_OTHER)에 남긴다.
     */
    const handleSelectEmployee = (v) => {
        setEmployee(v && typeof v !== "string" ? v : null);
        setTab("ALL"); setQ(""); setPage(0); setRows([]); setTotal(0);
    };

    useEffect(() => {
        loadCounts(employeeId);
        // eslint-disable-next-line
    }, [employeeId]);

    // 통합 재조회 — 직원·폴더·검색어(debounce)·페이지크기 어느 것이 바뀌어도 1페이지부터 다시.
    useEffect(() => {
        const t = setTimeout(() => { setPage(0); load(0); }, q ? 300 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [employeeId, tab, q, size]);

    const refresh = () => { load(); loadCounts(employeeId); };

    const COLS = 7;
    const totalMap  = counts?.total  || {};
    const unreadMap = counts?.unread || {};

    return (
        <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column",
                   overflow: "auto", ...ds.hiddenScrollbar }}>
            {/* 헤더 */}
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", flex: 1 }}>
                    직원 메일함
                    <Typography component="span" sx={{ ml: 1, fontSize: 12, color: "#94a3b8" }}>
                        {employee ? `${total}건 · 관리자` : "관리자"}
                    </Typography>
                </Typography>
                {employee && <TableSizeSelect value={size} onChange={(n) => { setSize(n); setPage(0); }} />}
                <Tooltip title="새로고침">
                    <span>
                        <IconButton size="small" onClick={refresh} disabled={!employee}>
                            <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            <Typography sx={{ fontSize: 12, color: "#64748b", mb: 1.5, lineHeight: 1.6 }}>
                직원을 선택하면 그 사람의 메일함을 <b>본인이 보는 폴더 그대로</b> 열람합니다
                (스팸함·휴지통·임시보관함 포함). 열람 사실은 감사 로그(READ_OTHER)에 대상 직원과 함께 기록됩니다.
            </Typography>

            {/* 직원 선택 — 사내 전체 주소록(재직자) 자동완성 */}
            <Box sx={{ maxWidth: 420, mb: 2 }}>
                <AppAutocomplete
                    value={employee}
                    onChange={handleSelectEmployee}
                    fetcher={async (kw) => {
                        const res = await mailApi.listCompanyContacts(kw || "", 0, 20);
                        const data = res?.data ?? res;
                        return data?.content ?? [];
                    }}
                    getOptionLabel={(o) => (typeof o === "string" ? o : (o?.name || ""))}
                    secondaryText={(o) => [o?.department, o?.position, o?.email].filter(Boolean).join(" · ")}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    placeholder="직원 이름 · 이메일 · 사번으로 검색"
                    startAdornment={<PersonSearchRoundedIcon sx={{ fontSize: 18, color: "#94a3b8", mr: 0.5 }} />}
                />
            </Box>

            {!employee ? (
                <Paper variant="outlined" sx={{ borderColor: "#e2e8f0", py: 8, textAlign: "center" }}>
                    <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
                        조회할 직원을 선택하세요.
                    </Typography>
                </Paper>
            ) : (
                <>
                    {/* 대상 직원 요약 — 지금 누구의 편지함을 보고 있는지 항상 눈에 보이게 */}
                    <Paper variant="outlined"
                           sx={{ borderColor: "#e2e8f0", bgcolor: "#f8fafc", px: 2, py: 1.2, mb: 1.5 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                                {counts?.employeeName || employee.name}
                            </Typography>
                            {(counts?.department || employee.department) && (
                                <Chip size="small" label={counts?.department || employee.department}
                                      sx={{ height: 20, fontSize: 11, fontWeight: 600,
                                            color: "#475569", bgcolor: "#e2e8f0" }} />
                            )}
                            {(counts?.position || employee.position) && (
                                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                                    {counts?.position || employee.position}
                                </Typography>
                            )}
                            <Typography sx={{ fontSize: 12.5, color: "#475569" }}>
                                {counts?.email || employee.email || "-"}
                            </Typography>
                        </Stack>
                    </Paper>

                    {/* 폴더 탭 — 라벨 옆 전체 건수, 안 읽음이 있으면 파란 뱃지 */}
                    <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
                          sx={{ minHeight: 36, mb: 1.5,
                                "& .MuiTab-root": { minHeight: 36, fontSize: 13, fontWeight: 600, textTransform: "none" } }}>
                        {TABS.map((t) => {
                            const tot = sumCounts(totalMap, t.countKeys);
                            const unread = sumCounts(unreadMap, t.countKeys);
                            return (
                                <Tab key={t.key} value={t.key} label={
                                    <Stack direction="row" spacing={0.6} alignItems="center">
                                        <span>{t.label}</span>
                                        <Typography component="span" sx={{ fontSize: 11.5, color: "#94a3b8" }}>{tot}</Typography>
                                        {unread > 0 && (
                                            <Chip label={unread} size="small"
                                                  sx={{ height: 16, fontSize: 10, fontWeight: 700,
                                                        bgcolor: ds.colors.primary, color: "#fff",
                                                        "& .MuiChip-label": { px: 0.6 } }} />
                                        )}
                                    </Stack>
                                } />
                            );
                        })}
                    </Tabs>

                    <TextField
                        fullWidth size="small" value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="제목 / 보낸이 검색 (선택한 폴더 안에서)"
                        InputProps={{ startAdornment: <SearchRoundedIcon sx={{ fontSize: 18, color: "#94a3b8", mr: 1 }} /> }}
                        sx={{ mb: 2 }}
                    />

                    {/* 표는 내부 스크롤 금지(패널 루트가 스크롤 담당, 시스템 공통 규칙) */}
                    <Paper variant="outlined" sx={{ borderColor: "#e2e8f0", overflowX: "clip", overflowY: "visible", flexShrink: 0 }}>
                        <ResizableTable size="small" sx={ds.tableSx} resetKey={tab}>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center" sx={{ width: 60, minWidth: 60 }}>번호</TableCell>
                                    <TableCell>보낸이</TableCell>
                                    <TableCell>제목</TableCell>
                                    <TableCell align="center">폴더</TableCell>
                                    <TableCell align="center">상태</TableCell>
                                    <TableCell align="center">시각</TableCell>
                                    <TableCell align="center">용량</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow><TableCell colSpan={COLS} align="center" sx={{ py: 4 }}><LoadingDots /></TableCell></TableRow>
                                ) : rows.length === 0 ? (
                                    <TableRow><TableCell colSpan={COLS} align="center" sx={{ py: 6, color: "#94a3b8", fontSize: 13 }}>
                                        {q ? "검색 결과가 없습니다" : "이 폴더에 메일이 없습니다"}
                                    </TableCell></TableRow>
                                ) : rows.map((m, idx) => {
                                    const unread = m.isRead === false;
                                    return (
                                        <TableRow key={m.id} hover onClick={() => onOpenMail?.(m.id)} sx={{ cursor: "pointer" }}>
                                            <TableCell align="center" sx={{ fontSize: 12, color: "#94a3b8" }}>{page * size + idx + 1}</TableCell>
                                            <TableCell sx={{ fontSize: 13, fontWeight: unread ? 700 : 400 }}>
                                                {m.fromName ? `${m.fromName} <${m.fromAddr || ""}>` : (m.fromAddr || "-")}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13, fontWeight: unread ? 700 : 400 }}>
                                                {m.isStarred && <StarRoundedIcon sx={{ fontSize: 14, color: "#f59e0b", mr: 0.4, verticalAlign: "-2px" }} />}
                                                {m.subject || "(제목 없음)"}
                                                {m.hasAttachments ? (
                                                    <Typography component="span" sx={{ ml: 0.5, fontSize: 11, color: "#94a3b8" }}>
                                                        📎{m.attachmentCount || ""}
                                                    </Typography>
                                                ) : null}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
                                                {m.customFolderName || FOLDER_LABEL[m.folder] || m.folder || "-"}
                                            </TableCell>
                                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                                                {/* 발신 계열은 배달 결과(반송·스팸신고)가, 수신 계열은 읽음 여부가 관심사다.
                                                    showHint 는 상세 화면용(사유를 아랫줄로 펼침) — 표에서는 tooltip 기본값을 쓴다 */}
                                                <DeliveryStatusBadge mail={m} />
                                                {unread && m.direction === "INBOUND" && (
                                                    <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, color: ds.colors.primary }}>
                                                        안읽음
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>
                                                {fmt(m.receivedAt)}
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>
                                                {formatFileSize(m.sizeBytes, "-")}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </ResizableTable>
                    </Paper>

                    <TablePaginator
                        total={total}
                        pageSize={size}
                        page={page + 1}
                        onPageChange={(p) => { const np = p - 1; setPage(np); load(np); }}
                    />
                </>
            )}
        </Box>
    );
}
